import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Snackbar,
  Chip
} from '@mui/material';
import { Lock, Person, Security } from '@mui/icons-material';
import { useComponentWebSocket } from './GlobalSettingUtils';
import QRCode from 'qrcode';
import { useTranslation } from 'react-i18next';

const AccountSettings = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaProvisioningUri, setMfaProvisioningUri] = useState('');
  const [mfaQrData, setMfaQrData] = useState('');
  const [mfaSetupCode, setMfaSetupCode] = useState('');
  const [mfaSetupError, setMfaSetupError] = useState('');
  const [mfaDisableCode, setMfaDisableCode] = useState('');
  const [mfaDisablePassword, setMfaDisablePassword] = useState('');
  const [mfaDisableError, setMfaDisableError] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [userInfo, setUserInfo] = useState({ username: '', permissions: '' });

  // Define consistent colors and styling
  const primaryColor = '#5757b0';

  // Define command handlers for WebSocket messages
  const commandHandlers = {
    'change_password': (message) => {
      setLoading(false);
      if (message.data.status === 'success') {
        setMessage({ type: 'success', text: message.data.message });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setMessage({ type: 'error', text: message.data.message });
      }
      setShowSnackbar(true);
    },
    'login_response': (message) => {
      console.log('Login response received:', message);
      if (message.data && message.data.success) {
        setUserInfo({
          username: message.data.username || '',
          permissions: message.data.permissions || ''
        });
      }
    },
    'session_validated': (message) => {
      console.log('Session validated message received:', message);
      if (message.data && message.data.success) {
        setUserInfo({
          username: message.data.username || '',
          permissions: message.data.permissions || ''
        });
      }
    },
    'validate_session': (message) => {
      console.log('Validate session message received:', message);
      // Handle both old format (valid) and new format (success)
      const isValid = message.data && (message.data.success || message.data.valid);
      if (isValid) {
        setUserInfo({
          username: message.data.username || '',
          permissions: message.data.permissions || ''
        });
      }
    },
    'get_mfa_status': (message) => {
      if (message.data?.success) {
        const enabled = !!message.data.enabled;
        const pending = !!message.data.pending;
        setMfaEnabled(enabled);
        setMfaPending(pending);
        if (!pending) {
          setMfaSecret('');
          setMfaSetupCode('');
          setMfaProvisioningUri('');
          setMfaQrData('');
        }
      } else if (message.data?.message) {
        setMessage({ type: 'error', text: message.data.message });
        setShowSnackbar(true);
      }
    },
    'begin_mfa_setup': (message) => {
      setMfaLoading(false);
      if (message.data?.success) {
        setMfaPending(true);
        setMfaSecret(message.data.secret || '');
        setMfaProvisioningUri(message.data.provisioning_uri || '');
        setMfaQrData('');
        setMfaSetupCode('');
        setMfaSetupError('');
        setMessage({ type: 'success', text: t('account_settings.mfa_setup_started') });
      } else {
        setMessage({ type: 'error', text: message.data?.message || t('account_settings.mfa_setup_failed') });
      }
      setShowSnackbar(true);
    },
    'verify_mfa_setup': (message) => {
      setMfaLoading(false);
      if (message.data?.success) {
        setMfaEnabled(true);
        setMfaPending(false);
        setMfaSecret('');
        setMfaProvisioningUri('');
        setMfaQrData('');
        setMfaSetupCode('');
        setMfaSetupError('');
        setMessage({ type: 'success', text: message.data?.message || t('account_settings.mfa_enabled_success') });
      } else {
        setMfaSetupError(message.data?.message || t('account_settings.mfa_invalid_code'));
        setMessage({ type: 'error', text: message.data?.message || t('account_settings.mfa_verify_failed') });
      }
      setShowSnackbar(true);
    },
    'disable_mfa': (message) => {
      setMfaLoading(false);
      if (message.data?.success) {
        setMfaEnabled(false);
        setMfaPending(false);
        setMfaSecret('');
        setMfaProvisioningUri('');
        setMfaQrData('');
        setMfaDisableCode('');
        setMfaDisablePassword('');
        setMfaDisableError('');
        setMessage({ type: 'success', text: message.data?.message || t('account_settings.mfa_disabled_success') });
      } else {
        setMfaDisableError(message.data?.message || t('account_settings.mfa_invalid_code'));
        setMessage({ type: 'error', text: message.data?.message || t('account_settings.mfa_disable_failed') });
      }
      setShowSnackbar(true);
    }
  };

  console.log('AccountSettings rendered with userInfo:', userInfo);
  
  const { sendCommand } = useComponentWebSocket(commandHandlers);

  useEffect(() => {
    if (userInfo.username) {
      sendCommand('get_mfa_status', {});
    }
  }, [userInfo.username, sendCommand]);

  useEffect(() => {
    let isActive = true;
    if (!mfaProvisioningUri) {
      setMfaQrData('');
      return () => {};
    }

    setMfaQrData('');
    QRCode.toDataURL(mfaProvisioningUri, { margin: 1, width: 200 })
      .then((url) => {
        if (isActive) setMfaQrData(url);
      })
      .catch((err) => {
        console.error('Failed to generate MFA QR code', err);
        if (isActive) setMfaQrData('');
      });

    return () => {
      isActive = false;
    };
  }, [mfaProvisioningUri]);

  // Request current session validation when component mounts
  useEffect(() => {
    console.log('AccountSettings mounted...');
    // Check if user is logged in and has session token
    const isLoggedIn = localStorage.getItem('dataunfold_logged_in') === 'true';
    const sessionToken = localStorage.getItem('dataunfold_session_token');
    
    if (isLoggedIn && sessionToken) {
      console.log('User is logged in, requesting session validation for user info...');
      // Request session validation to get user info
      sendCommand('validate_session', { session_token: sessionToken });
    } else {
      console.log('User not logged in or no session token found');
    }
  }, [sendCommand]);  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    // You can add any methods here if needed for parent component interaction
  }), []);

  const handlePasswordChange = async () => {
    // Validate form
    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: t('account_settings.all_fields_required') });
      setShowSnackbar(true);
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: t('account_settings.passwords_not_match') });
      setShowSnackbar(true);
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: t('account_settings.password_too_short') });
      setShowSnackbar(true);
      return;
    }

    if (newPassword === currentPassword) {
      setMessage({ type: 'error', text: t('account_settings.password_must_differ') });
      setShowSnackbar(true);
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    // Send password change request
    sendCommand('change_password', {
      currentPassword,
      newPassword
    });
  };

  const handleBeginMfaSetup = () => {
    setMfaLoading(true);
    setMessage({ type: '', text: '' });
    sendCommand('begin_mfa_setup', {});
  };

  const handleVerifyMfaSetup = () => {
    if (!mfaSetupCode.trim()) {
      setMessage({ type: 'error', text: t('account_settings.mfa_code_required') });
      setShowSnackbar(true);
      return;
    }
    setMfaSetupError('');
    setMfaLoading(true);
    sendCommand('verify_mfa_setup', { code: mfaSetupCode.trim() });
  };

  const handleDisableMfa = () => {
    if (!mfaDisablePassword.trim() || !mfaDisableCode.trim()) {
      setMessage({ type: 'error', text: t('account_settings.mfa_password_code_required') });
      setShowSnackbar(true);
      return;
    }
    setMfaDisableError('');
    setMfaLoading(true);
    sendCommand('disable_mfa', {
      password: mfaDisablePassword,
      code: mfaDisableCode.trim()
    });
  };

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  const mfaStatusLabel = mfaEnabled ? t('account_settings.mfa_status_enabled') : (mfaPending ? t('account_settings.mfa_status_pending') : t('account_settings.mfa_status_disabled'));
  const mfaStatusColor = mfaEnabled ? 'success' : (mfaPending ? 'warning' : 'default');
  const compactFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fafafa',
      '&.Mui-focused fieldset': {
        borderColor: primaryColor
      }
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: primaryColor
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#222', fontWeight: 600, mb: 0.5 }}>
          {t('account_settings.title')}
        </Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>
          {t('account_settings.subtitle')}
        </Typography>
      </Box>

      {userInfo.username && (
        <Box sx={{ mb: 2.5, p: 2, borderRadius: 1.5, backgroundColor: '#f7f8fc' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    backgroundColor: '#e7e8f8',
                    color: primaryColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Person sx={{ fontSize: '1.2rem' }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    {t('account_settings.logged_in_as')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {userInfo.username}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'center', gap: 1 }}>
                <Security sx={{ color: '#666', fontSize: '1.1rem' }} />
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {t('account_settings.permission_level')}
                </Typography>
                <Chip
                  label={userInfo.permissions || 'user'}
                  size="small"
                  color={userInfo.permissions === 'admin' ? 'primary' : 'default'}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      <Box sx={{ p: { xs: 0, sm: 1 } }}>
        <Typography
          variant="subtitle1"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            color: '#333',
            fontWeight: 600
          }}
        >
          <Lock sx={{ color: primaryColor, fontSize: '1.1rem' }} />
          {t('account_settings.change_password_title')}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="password"
              label={t('account_settings.current_password')}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              variant="outlined"
              disabled={loading}
              placeholder={t('account_settings.current_password_placeholder')}
              sx={compactFieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="password"
              label={t('account_settings.new_password')}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              variant="outlined"
              disabled={loading}
              placeholder={t('account_settings.new_password_placeholder')}
              helperText={t('account_settings.new_password_helper')}
              sx={compactFieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="password"
              label={t('account_settings.confirm_new_password')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              variant="outlined"
              disabled={loading}
              placeholder={t('account_settings.confirm_new_password_placeholder')}
              helperText={t('account_settings.confirm_new_password_helper')}
              sx={compactFieldSx}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ p: 2, borderRadius: 1.5, backgroundColor: '#f7f8fc', mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: primaryColor }}>
                {t('account_settings.password_requirements_title')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                {t('account_settings.password_requirement_min')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                {t('account_settings.password_requirement_different')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handlePasswordChange}
                disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                sx={{
                  textTransform: 'none',
                  borderRadius: 1.5,
                  px: 2.5,
                  py: 1,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' }
                }}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Lock />}
              >
                {loading ? t('account_settings.changing_password') : t('account_settings.change_password_button')}
              </Button>
            </Box>

            <Divider sx={{ my: 2.5 }} />

            <Box sx={{ p: 2, borderRadius: 1.5, backgroundColor: '#f7f8fc' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Security sx={{ color: primaryColor, fontSize: '1.1rem' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                    {t('account_settings.mfa_title')}
                  </Typography>
                </Box>
                <Chip label={mfaStatusLabel} color={mfaStatusColor} size="small" sx={{ fontWeight: 600 }} />
              </Box>

              <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                {t('account_settings.mfa_subtitle')}
              </Typography>

              {!mfaEnabled ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {mfaPending && !mfaSecret && (
                    <Alert severity="info">
                      {t('account_settings.mfa_setup_pending_alert')}
                    </Alert>
                  )}
                  <Button
                    variant="outlined"
                    onClick={handleBeginMfaSetup}
                    disabled={mfaLoading}
                    sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                  >
                    {mfaLoading ? t('account_settings.mfa_generating') : t('account_settings.mfa_generate_secret')}
                  </Button>

                  {mfaSecret && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {mfaProvisioningUri && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          {mfaQrData ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                              <Box
                                component="img"
                                src={mfaQrData}
                                alt="MFA QR code"
                                sx={{ width: 180, height: 180, borderRadius: 1 }}
                              />
                              <Typography variant="caption" sx={{ color: '#666' }}>
                                {t('account_settings.mfa_scan_qr')}
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={20} />
                              <Typography variant="body2" sx={{ color: '#666' }}>
                                {t('account_settings.mfa_generating_qr')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                      <TextField
                        fullWidth
                        label={t('account_settings.mfa_authenticator_secret')}
                        value={mfaSecret}
                        InputProps={{ readOnly: true }}
                        helperText={t('account_settings.mfa_secret_helper')}
                        sx={compactFieldSx}
                      />
                      <TextField
                        fullWidth
                        label={t('account_settings.mfa_verification_code')}
                        value={mfaSetupCode}
                        onChange={(e) => {
                          setMfaSetupCode(e.target.value);
                          if (mfaSetupError) setMfaSetupError('');
                        }}
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        error={!!mfaSetupError}
                        helperText={mfaSetupError || ' '}
                        sx={compactFieldSx}
                      />
                      <Button
                        variant="contained"
                        onClick={handleVerifyMfaSetup}
                        disabled={mfaLoading || !mfaSetupCode.trim()}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none', borderRadius: 1.5, boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
                      >
                        {mfaLoading ? t('account_settings.mfa_verifying') : t('account_settings.mfa_verify_enable')}
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    type="password"
                    label={t('account_settings.current_password')}
                    value={mfaDisablePassword}
                    onChange={(e) => setMfaDisablePassword(e.target.value)}
                    sx={compactFieldSx}
                  />
                  <TextField
                    fullWidth
                    label={t('account_settings.mfa_authenticator_code')}
                    value={mfaDisableCode}
                    onChange={(e) => {
                      setMfaDisableCode(e.target.value);
                      if (mfaDisableError) setMfaDisableError('');
                    }}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    error={!!mfaDisableError}
                    helperText={mfaDisableError || ' '}
                    sx={compactFieldSx}
                  />
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDisableMfa}
                    disabled={mfaLoading || !mfaDisablePassword.trim() || !mfaDisableCode.trim()}
                    sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                  >
                    {mfaLoading ? t('account_settings.mfa_disabling') : t('account_settings.mfa_disable')}
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={message.type}
          sx={{
            width: '100%',
            borderRadius: 2,
            boxShadow: '0px 4px 12px rgba(0,0,0,0.15)',
            '& .MuiAlert-icon': {
              fontSize: '1.5rem'
            }
          }}
        >
          {message.text}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default AccountSettings;
